import React from 'react';
import { Box, Container, Typography, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { DESIGN_TOKENS } from '../../theme/designTokens';

const FooterContainer = styled('footer')(({ theme }) => ({
  background: '#FEFEFE',
  borderTop: '1px solid #E5E7EB',
  padding: theme.spacing(DESIGN_TOKENS.spacing.section.md, 0, DESIGN_TOKENS.spacing.section.sm, 0),
  marginTop: 'auto',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(DESIGN_TOKENS.spacing.section.sm, 0, 4, 0),
  },
}));

const FooterSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.5),
  },
}));

// FooterLink styles applied via sx prop instead of styled component
const footerLinkStyles = {
  color: 'text.secondary',
  textDecoration: 'none',
  fontSize: '0.875rem',
  fontWeight: 500,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    color: 'primary.main',
    textDecoration: 'none',
    transform: 'translateX(2px)',
  },
};

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
}));

const FooterLogoImage = styled('img')(({ theme }) => ({
  height: '36px',
  width: 'auto',
  
  [theme.breakpoints.down('sm')]: {
    height: '32px',
  },
}));

const FooterLogoText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Montserrat", sans-serif !important',
  fontWeight: 200,
  fontSize: '1.25rem',
  color: '#111827',
  letterSpacing: '-0.025em',
  whiteSpace: 'nowrap',
  
  '& strong': {
    fontWeight: 600,
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const Footer = () => {
  const { t } = useTranslation();

  return (
    <FooterContainer role="contentinfo">
      <Container maxWidth="lg">
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            },
            gap: { xs: 4, md: 6 }
          }}
        >
          <FooterSection>
            <LogoContainer>
              <FooterLogoImage
                src="/images/GembaScan-logo.jpeg"
                alt="Gemba Scan logo"
              />
              <FooterLogoText>
                <strong>Gemba</strong> Scan
              </FooterLogoText>
            </LogoContainer>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                lineHeight: 1.6,
                maxWidth: '280px' 
              }}
            >
              {t('footer.description')}
            </Typography>
          </FooterSection>

          <FooterSection>
            <nav role="navigation" aria-label="Footer navigation">
              <Typography 
                variant="h2"
                component="h2"
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: '1rem',
                  marginBottom: 1
                }}
              >
                {t('footer.navigation.title')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Link component={RouterLink} to="/" sx={footerLinkStyles}>
                  {t('footer.navigation.home')}
                </Link>
                <Link component={RouterLink} to="/upload" sx={footerLinkStyles}>
                  {t('footer.navigation.upload')}
                </Link>
                <Link component={RouterLink} to="/how-it-works" sx={footerLinkStyles}>
                  {t('footer.navigation.howItWorks')}
                </Link>
                <Link component={RouterLink} to="/about" sx={footerLinkStyles}>
                  {t('footer.navigation.about')}
                </Link>
              </Box>
            </nav>
          </FooterSection>

          <FooterSection>
            <Typography 
              variant="subtitle1" 
              component="div"
              sx={{ 
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '1rem',
                marginBottom: 1
              }}
            >
              {t('footer.contact.title')}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.875rem',
                lineHeight: 1.6
              }}
            >
              info@gembascan.com
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.875rem',
                lineHeight: 1.6
              }}
            >
              (555) 123-4567
            </Typography>
          </FooterSection>
        </Box>

        <Box 
          sx={{ 
            mt: { xs: 6, md: 8 },
            pt: { xs: 4, md: 6 },
            borderTop: '1px solid #E5E7EB'
          }}
        >
          <Typography 
            variant="body2" 
            align="center"
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.875rem'
            }}
          >
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </Typography>
        </Box>
      </Container>
    </FooterContainer>
  );
};

export default Footer; 